import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import {
  Package, Plus, Search, ShoppingCart, ArrowLeft, Pencil, Trash2,
  TrendingUp, TrendingDown, AlertTriangle, IndianRupee, Filter, X,
  RotateCcw, ArrowUpRight, ArrowDownRight, Loader2, Check, RefreshCw, Download, Undo2
} from 'lucide-react';

const DEFAULT_CATEGORIES = ['drinks', 'equipment', 'rentals', 'misc'];

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
];

// ========== Product Form ==========
const ProductForm = ({ product, onSave, onCancel, categories }) => {
  const [form, setForm] = useState(product || {
    name: '', category: 'drinks', sku: '', unit_type: 'piece',
    selling_price: '', low_stock_threshold: 5, is_rental: false, is_active: true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...form, selling_price: parseFloat(form.selling_price), low_stock_threshold: parseInt(form.low_stock_threshold) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-600">Product Name *</label>
          <input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="product-name-input" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">Category</label>
          <select className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="product-category-select">
            {categories.map(c => <option key={c} value={c} className="capitalize">{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">Selling Price *</label>
          <input type="number" min="0" step="0.01" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.selling_price} onChange={e => setForm({ ...form, selling_price: e.target.value })} required data-testid="product-price-input" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">Unit Type</label>
          <select className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.unit_type} onChange={e => setForm({ ...form, unit_type: e.target.value })}>
            <option value="piece">Piece</option>
            <option value="bottle">Bottle</option>
            <option value="can">Can</option>
            <option value="hourly_rental">Hourly Rental</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">SKU (Optional)</label>
          <input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600">Low Stock Alert</label>
          <input type="number" min="0" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.low_stock_threshold} onChange={e => setForm({ ...form, low_stock_threshold: e.target.value })} />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.is_rental} onChange={e => setForm({ ...form, is_rental: e.target.checked })} className="rounded" />
          Rental Item
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded" />
          Active
        </label>
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving || !form.name || !form.selling_price} data-testid="product-save-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          {product ? 'Update' : 'Add Product'}
        </Button>
      </div>
    </form>
  );
};

// ========== Products Tab ==========
const ProductsTab = ({ products, onRefresh, categories, onAddCategory }) => {
  // toast imported at top
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [newCat, setNewCat] = useState('');

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === 'all' || p.category === catFilter;
    return matchSearch && matchCat;
  });

  const handleSave = async (data) => {
    try {
      if (editProduct) {
        await api.request(`/inventory/products/${editProduct.id}`, { method: 'PUT', body: JSON.stringify(data) });
        toast({ title: 'Product updated' });
      } else {
        await api.request('/inventory/products', { method: 'POST', body: JSON.stringify(data) });
        toast({ title: 'Product added' });
      }
      setShowForm(false);
      setEditProduct(null);
      onRefresh();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.request(`/inventory/products/${id}`, { method: 'DELETE' });
      toast({ title: 'Product deleted' });
      onRefresh();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  if (showForm || editProduct) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditProduct(null); }}><ArrowLeft className="h-4 w-4" /></Button>
          <h3 className="font-semibold">{editProduct ? 'Edit Product' : 'Add Product'}</h3>
        </div>
        <ProductForm product={editProduct} onSave={handleSave} onCancel={() => { setShowForm(false); setEditProduct(null); }} categories={categories} />
      </div>
    );
  }

  return (
    <div data-testid="products-tab">
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} data-testid="product-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select className="border rounded-lg px-3 py-2 text-sm" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c} value={c} className="capitalize">{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
          <Button size="sm" className="gap-1.5" onClick={() => setShowForm(true)} data-testid="add-product-btn">
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>
      </div>
      {/* Add Category */}
      <div className="flex items-center gap-2 mb-4">
        <input className="border rounded-lg px-3 py-1.5 text-sm flex-1" placeholder="New category name..." value={newCat} onChange={e => setNewCat(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newCat.trim()) { onAddCategory(newCat.trim()); setNewCat(''); }}} data-testid="new-category-input" />
        <Button variant="outline" size="sm" disabled={!newCat.trim()} onClick={() => { onAddCategory(newCat.trim()); setNewCat(''); }} data-testid="add-category-btn">
          <Plus className="h-3.5 w-3.5" /> Category
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No products yet. Add your first product.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => (
            <div key={p.id} className={`bg-white border rounded-xl p-3 flex items-center justify-between ${!p.is_active ? 'opacity-50' : ''}`} data-testid={`product-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate">{p.name}</span>
                  {p.is_rental && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium">Rental</span>}
                  <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full capitalize">{p.category}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  <span className="flex items-center gap-0.5"><IndianRupee className="h-3 w-3" />{p.selling_price}</span>
                  <span className={`font-medium ${p.current_stock <= p.low_stock_threshold ? 'text-red-600' : 'text-green-600'}`}>
                    Stock: {p.current_stock}
                  </span>
                  {p.avg_cost > 0 && <span>Avg Cost: {p.avg_cost}</span>}
                </div>
              </div>
              <div className="flex gap-1 ml-2">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEditProduct(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => handleDelete(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========== POS Sales Tab ==========
const POSTab = ({ products, onRefresh }) => {
  // toast imported at top
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [processing, setProcessing] = useState(false);

  const activeProducts = products.filter(p => p.is_active && !p.is_rental);
  const filtered = activeProducts.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(c => c.product_id === product.id);
      if (existing) {
        return prev.map(c => c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, { product_id: product.id, product_name: product.name, quantity: 1, selling_price: product.selling_price, max_stock: product.current_stock }];
    });
  };

  const updateQty = (productId, qty) => {
    if (qty <= 0) {
      setCart(prev => prev.filter(c => c.product_id !== productId));
    } else {
      setCart(prev => prev.map(c => c.product_id === productId ? { ...c, quantity: qty } : c));
    }
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.quantity * c.selling_price, 0);

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setProcessing(true);
    try {
      for (const item of cart) {
        await api.request('/inventory/sales', {
          method: 'POST',
          body: JSON.stringify({
            product_id: item.product_id,
            quantity: item.quantity,
            selling_price: item.selling_price,
            payment_mode: paymentMode,
          }),
        });
      }
      toast({ title: 'Sale Recorded', description: `${cart.length} item(s) sold for Rs ${cartTotal}` });
      setCart([]);
      onRefresh();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4" data-testid="pos-tab">
      {/* Product Grid */}
      <div className="flex-1">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} data-testid="pos-search" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {filtered.map(p => (
            <button
              key={p.id}
              onClick={() => p.current_stock > 0 && addToCart(p)}
              className={`text-left border rounded-xl p-3 transition-all ${p.current_stock > 0 ? 'hover:border-blue-400 hover:shadow-sm active:scale-[0.98] cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
              disabled={p.current_stock <= 0}
              data-testid={`pos-product-${p.id}`}
            >
              <p className="font-medium text-sm truncate">{p.name}</p>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-xs font-semibold text-green-700 flex items-center"><IndianRupee className="h-3 w-3" />{p.selling_price}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${p.current_stock <= p.low_stock_threshold ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                  {p.current_stock} left
                </span>
              </div>
            </button>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No products found</p>}
      </div>

      {/* Cart */}
      <div className="lg:w-72 xl:w-80 bg-white border rounded-xl p-4 h-fit lg:sticky lg:top-4" data-testid="pos-cart">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
          <ShoppingCart className="h-4 w-4" /> Cart ({cart.length})
        </h3>
        {cart.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">Tap a product to add</p>
        ) : (
          <div className="space-y-2 mb-4">
            {cart.map(item => (
              <div key={item.product_id} className="flex items-center gap-2 text-sm">
                <span className="flex-1 truncate">{item.product_name}</span>
                <div className="flex items-center gap-1">
                  <button className="w-6 h-6 rounded bg-gray-100 text-xs font-bold" onClick={() => updateQty(item.product_id, item.quantity - 1)}>-</button>
                  <span className="w-6 text-center text-xs font-medium">{item.quantity}</span>
                  <button className="w-6 h-6 rounded bg-gray-100 text-xs font-bold" onClick={() => updateQty(item.product_id, Math.min(item.quantity + 1, item.max_stock))}>+</button>
                </div>
                <span className="w-14 text-right text-xs font-medium">{item.quantity * item.selling_price}</span>
              </div>
            ))}
          </div>
        )}

        <div className="border-t pt-3">
          <div className="flex justify-between font-semibold text-sm mb-3">
            <span>Total</span>
            <span className="flex items-center"><IndianRupee className="h-3.5 w-3.5" />{cartTotal}</span>
          </div>
          <div className="flex gap-1.5 mb-3">
            {PAYMENT_MODES.map(m => (
              <button
                key={m.value}
                onClick={() => setPaymentMode(m.value)}
                className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors ${paymentMode === m.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}
                data-testid={`payment-${m.value}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <Button className="w-full gap-2" onClick={handleCheckout} disabled={cart.length === 0 || processing} data-testid="checkout-btn">
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Sell ({paymentMode.toUpperCase()})
          </Button>
        </div>
      </div>
    </div>
  );
};

// ========== Purchases Tab ==========
const PurchasesTab = ({ products, onRefresh }) => {
  // toast imported at top
  const [purchases, setPurchases] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ product_id: '', quantity: '', purchase_price: '', supplier: '', notes: '' });

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    setLoading(true);
    try {
      const data = await api.request('/inventory/purchases');
      setPurchases(data);
    } catch { }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.request('/inventory/purchases', {
        method: 'POST',
        body: JSON.stringify({ ...form, quantity: parseInt(form.quantity), purchase_price: parseFloat(form.purchase_price) }),
      });
      toast({ title: 'Purchase recorded', description: 'Stock updated automatically' });
      setShowForm(false);
      setForm({ product_id: '', quantity: '', purchase_price: '', supplier: '', notes: '' });
      loadPurchases();
      onRefresh();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <div data-testid="purchases-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-sm">Purchase History (Stock In)</h3>
        <Button size="sm" className="gap-1.5" onClick={() => setShowForm(!showForm)} data-testid="add-purchase-btn">
          <Plus className="h-3.5 w-3.5" /> Log Purchase
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 space-y-3" data-testid="purchase-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Product *</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.product_id} onChange={e => setForm({ ...form, product_id: e.target.value })} required>
                <option value="">Select product</option>
                {products.filter(p => p.is_active).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Quantity *</label>
              <input type="number" min="1" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} required />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Purchase Price (per unit) *</label>
              <input type="number" min="0" step="0.01" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.purchase_price} onChange={e => setForm({ ...form, purchase_price: e.target.value })} required />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Supplier</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} />
            </div>
          </div>
          {form.quantity && form.purchase_price && (
            <p className="text-sm font-medium text-blue-700">Total: Rs {(parseInt(form.quantity || 0) * parseFloat(form.purchase_price || 0)).toFixed(2)}</p>
          )}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={!form.product_id || !form.quantity || !form.purchase_price}>Record Purchase</Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : purchases.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-8">No purchases recorded yet</p>
      ) : (
        <div className="space-y-2">
          {purchases.map(p => {
            const reverse = async () => {
              if (p.reversed) return;
              const reason = window.prompt(
                `Reverse ${p.quantity} unit(s) of ${p.product_name}?\n` +
                `Current stock will reduce by ${p.quantity}.\n\n` +
                `Reason (optional, will be recorded in stock ledger):`,
                ''
              );
              if (reason === null) return;   // user cancelled
              try {
                const res = await api.request(`/inventory/purchases/${p.id}/reverse`, {
                  method: 'POST',
                  body: JSON.stringify({ reason: reason.trim() || null }),
                });
                toast({
                  title: 'Purchase reversed',
                  description: `Stock reduced by ${res.reversed_quantity}. New stock: ${res.new_stock}.`,
                });
                loadPurchases();
                onRefresh && onRefresh();
              } catch (e) {
                toast({
                  title: 'Could not reverse',
                  description: e.message || 'Please try again.',
                  variant: 'destructive',
                });
              }
            };
            return (
              <div
                key={p.id}
                className={`border rounded-xl p-3 flex items-center justify-between ${p.reversed ? 'bg-gray-50 border-gray-200 opacity-75' : 'bg-white'}`}
                data-testid={`purchase-${p.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium ${p.reversed ? 'line-through text-gray-500' : ''}`}>
                      {p.product_name}
                    </p>
                    {p.reversed && (
                      <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded uppercase font-semibold" data-testid={`purchase-reversed-${p.id}`}>
                        Reversed
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {p.date} {p.supplier ? `from ${p.supplier}` : ''}
                  </p>
                  {p.reversed && p.reverse_reason && (
                    <p className="text-[11px] text-gray-500 mt-0.5">Reason: {p.reverse_reason}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-right">
                    <p className={`text-sm font-medium flex items-center justify-end gap-0.5 ${p.reversed ? 'text-gray-400' : 'text-green-700'}`}>
                      <ArrowUpRight className="h-3.5 w-3.5" /> +{p.quantity}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center justify-end">
                      <IndianRupee className="h-3 w-3" />{p.total_cost}
                    </p>
                  </div>
                  {!p.reversed && (
                    <button
                      onClick={reverse}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      title="Reverse this stock-in"
                      data-testid={`reverse-purchase-${p.id}`}
                    >
                      <Undo2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ========== Rentals Tab ==========
const RentalsTab = ({ products, onRefresh }) => {
  // toast imported at top
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIssue, setShowIssue] = useState(false);
  const [form, setForm] = useState({ product_id: '', quantity: 1 });

  const rentalProducts = products.filter(p => p.is_rental && p.is_active);

  useEffect(() => { loadRentals(); }, []);

  const loadRentals = async () => {
    setLoading(true);
    try {
      const data = await api.request('/inventory/rentals');
      setRentals(data);
    } catch { }
    setLoading(false);
  };

  const handleIssue = async (e) => {
    e.preventDefault();
    try {
      await api.request('/inventory/rentals', {
        method: 'POST',
        body: JSON.stringify({ ...form, quantity: parseInt(form.quantity), action: 'issue' }),
      });
      toast({ title: 'Issued', description: 'Rental item issued' });
      setShowIssue(false);
      setForm({ product_id: '', quantity: 1 });
      loadRentals();
      onRefresh();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleAction = async (productId, action) => {
    try {
      await api.request('/inventory/rentals', {
        method: 'POST',
        body: JSON.stringify({ product_id: productId, quantity: 1, action }),
      });
      toast({ title: action === 'return' ? 'Returned' : 'Marked Lost' });
      loadRentals();
      onRefresh();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const activeRentals = rentals.filter(r => r.status === 'issued');

  return (
    <div data-testid="rentals-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-sm">Rentals ({activeRentals.length} active)</h3>
        <Button size="sm" className="gap-1.5" onClick={() => setShowIssue(!showIssue)} data-testid="issue-rental-btn">
          <Plus className="h-3.5 w-3.5" /> Issue
        </Button>
      </div>

      {showIssue && (
        <form onSubmit={handleIssue} className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Rental Item</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.product_id} onChange={e => setForm({ ...form, product_id: e.target.value })} required>
                <option value="">Select</option>
                {rentalProducts.map(p => <option key={p.id} value={p.id}>{p.name} (Stock: {p.current_stock})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Quantity</label>
              <input type="number" min="1" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} required />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowIssue(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={!form.product_id}>Issue</Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : activeRentals.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-8">No active rentals</p>
      ) : (
        <div className="space-y-2">
          {activeRentals.map(r => {
            const outstanding = r.issued_quantity - r.returned_quantity - r.lost_quantity;
            return (
              <div key={r.id} className="bg-white border rounded-xl p-3" data-testid={`rental-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{r.product_name}</p>
                    <p className="text-xs text-gray-500">Issued: {r.issued_quantity} | Returned: {r.returned_quantity} | Lost: {r.lost_quantity} | Outstanding: <strong>{outstanding}</strong></p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-green-700" onClick={() => handleAction(r.product_id, 'return')}>
                      <RotateCcw className="h-3 w-3" /> Return
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-600" onClick={() => handleAction(r.product_id, 'lost')}>
                      <X className="h-3 w-3" /> Lost
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ========== Dashboard Tab ==========
const DashboardTab = ({ products }) => {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('today');
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.request(`/inventory/dashboard?period=${period}`);
      setData(d);
    } catch { }
    setLoading(false);
  }, [period]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  if (loading || !data) return <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div data-testid="dashboard-tab">
      {/* Period selector */}
      <div className="flex gap-1.5 mb-4">
        {['today', 'week', 'month', 'all'].map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors capitalize ${period === p ? 'bg-[#0A2C93] text-white border-[#0A2C93]' : 'bg-white text-gray-600'}`}
          >
            {p === 'all' ? 'All Time' : p === 'week' ? '7 Days' : p === 'month' ? '30 Days' : 'Today'}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500">Revenue</p>
          <p className="text-lg font-bold text-green-700 flex items-center"><IndianRupee className="h-4 w-4" />{data.total_revenue.toLocaleString()}</p>
          <p className="text-[10px] text-gray-400">{data.total_sales_count} sales</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500">Purchase Cost</p>
          <p className="text-lg font-bold text-red-600 flex items-center"><IndianRupee className="h-4 w-4" />{data.total_purchase_cost.toLocaleString()}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500">Est. Profit</p>
          <p className={`text-lg font-bold flex items-center ${data.estimated_profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
            <IndianRupee className="h-4 w-4" />{data.estimated_profit.toLocaleString()}
          </p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500">Low Stock</p>
          <p className={`text-lg font-bold ${data.low_stock_alerts.length > 0 ? 'text-amber-600' : 'text-green-700'}`}>{data.low_stock_alerts.length}</p>
          <p className="text-[10px] text-gray-400">items below threshold</p>
        </div>
      </div>

      {/* Top Sellers & Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-4">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-green-600" /> Top Sellers</h4>
          {data.top_sellers.length === 0 ? (
            <p className="text-xs text-gray-400">No sales in this period</p>
          ) : (
            <div className="space-y-2">
              {data.top_sellers.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="truncate">{s.product_name}</span>
                  <div className="text-right text-xs">
                    <span className="font-medium">{s.quantity} sold</span>
                    <span className="text-gray-400 ml-2">Rs {s.revenue}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border rounded-xl p-4">
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-amber-500" /> Low Stock Alerts</h4>
          {data.low_stock_alerts.length === 0 ? (
            <p className="text-xs text-gray-400">All items well-stocked</p>
          ) : (
            <div className="space-y-2">
              {data.low_stock_alerts.map((a, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="truncate">{a.product_name}</span>
                  <span className="text-red-600 font-medium text-xs">{a.current_stock} / {a.threshold}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Payment Breakdown */}
      {Object.keys(data.payment_breakdown).length > 0 && (
        <div className="bg-white border rounded-xl p-4 mt-4">
          <h4 className="text-sm font-semibold mb-3">Payment Breakdown</h4>
          <div className="flex gap-4">
            {Object.entries(data.payment_breakdown).map(([mode, info]) => (
              <div key={mode} className="flex-1 text-center">
                <p className="text-xs text-gray-500 uppercase">{mode}</p>
                <p className="text-sm font-bold flex items-center justify-center"><IndianRupee className="h-3 w-3" />{info.amount.toLocaleString()}</p>
                <p className="text-[10px] text-gray-400">{info.count} txns</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ========== Reports Tab ==========
const ReportsTab = () => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await api.request(`/inventory/reports/sales?start_date=${startDate}&end_date=${endDate}`);
      setReport(data);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { loadReport(); }, []);

  const downloadCSV = () => {
    if (!report || !report.rows.length) return;
    const headers = ['Date', 'Product', 'Category', 'Quantity Sold', 'Revenue (Rs)', 'Cash', 'UPI', 'Card'];
    const csvRows = [headers.join(',')];
    for (const r of report.rows) {
      csvRows.push([
        r.date,
        `"${r.product_name}"`,
        r.category,
        r.quantity,
        r.revenue.toFixed(2),
        (r.payment_modes.cash || 0).toFixed(2),
        (r.payment_modes.upi || 0).toFixed(2),
        (r.payment_modes.card || 0).toFixed(2),
      ].join(','));
    }
    csvRows.push('');
    csvRows.push(`Total,,, ${report.total_items_sold}, ${report.total_revenue.toFixed(2)}`);

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-report-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="reports-tab">
      <h3 className="font-semibold text-sm mb-3">Sales Report</h3>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">From</label>
          <input type="date" className="border rounded-lg px-3 py-2 text-sm" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="report-start-date" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">To</label>
          <input type="date" className="border rounded-lg px-3 py-2 text-sm" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="report-end-date" />
        </div>
        <Button size="sm" className="gap-1.5" onClick={loadReport} disabled={loading} data-testid="generate-report-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Generate
        </Button>
        {report && report.rows.length > 0 && (
          <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadCSV} data-testid="download-csv-btn">
            <Download className="h-3.5 w-3.5" /> Download CSV
          </Button>
        )}
      </div>

      {/* Quick presets */}
      <div className="flex gap-1.5 mb-4">
        {[
          { label: 'Today', fn: () => { const t = new Date().toISOString().slice(0,10); setStartDate(t); setEndDate(t); }},
          { label: '7 Days', fn: () => { const d = new Date(); const e = d.toISOString().slice(0,10); d.setDate(d.getDate()-7); setStartDate(d.toISOString().slice(0,10)); setEndDate(e); }},
          { label: '30 Days', fn: () => { const d = new Date(); const e = d.toISOString().slice(0,10); d.setDate(d.getDate()-30); setStartDate(d.toISOString().slice(0,10)); setEndDate(e); }},
          { label: 'This Month', fn: () => { const d = new Date(); setStartDate(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`); setEndDate(d.toISOString().slice(0,10)); }},
        ].map(p => (
          <button key={p.label} onClick={() => { p.fn(); }} className="px-3 py-1 text-xs border rounded-lg hover:bg-gray-50 text-gray-600">{p.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : !report ? (
        <p className="text-center text-gray-400 text-sm py-8">Select a date range and click Generate</p>
      ) : report.rows.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-8">No sales data for this period</p>
      ) : (
        <div>
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-white border rounded-xl p-3">
              <p className="text-xs text-gray-500">Total Items Sold</p>
              <p className="text-lg font-bold">{report.total_items_sold}</p>
            </div>
            <div className="bg-white border rounded-xl p-3">
              <p className="text-xs text-gray-500">Total Revenue</p>
              <p className="text-lg font-bold text-green-700 flex items-center"><IndianRupee className="h-4 w-4" />{report.total_revenue.toLocaleString()}</p>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border rounded-xl overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Product</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Category</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Qty</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2 text-gray-600">{r.date}</td>
                    <td className="px-3 py-2 font-medium">{r.product_name}</td>
                    <td className="px-3 py-2 capitalize text-gray-500">{r.category}</td>
                    <td className="px-3 py-2 text-right">{r.quantity}</td>
                    <td className="px-3 py-2 text-right font-medium text-green-700 flex items-center justify-end"><IndianRupee className="h-3 w-3" />{r.revenue.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== Main Component ==========
const InventoryManagement = () => {
  const [activeTab, setActiveTab] = useState('pos');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(async () => {
    try {
      const data = await api.request('/inventory/products');
      setProducts(data);
    } catch { }
    setLoading(false);
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const data = await api.request('/inventory/categories');
      setCategories(data);
    } catch { }
  }, []);

  useEffect(() => { loadProducts(); loadCategories(); }, [loadProducts, loadCategories]);

  const handleAddCategory = async (name) => {
    try {
      await api.request('/inventory/categories', { method: 'POST', body: JSON.stringify({ name }) });
      toast({ title: 'Category added', description: name });
      loadCategories();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const tabs = [
    { id: 'pos', label: 'Quick Sale', icon: ShoppingCart },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'purchases', label: 'Stock In', icon: ArrowUpRight },
    { id: 'rentals', label: 'Rentals', icon: RefreshCw },
    { id: 'reports', label: 'Reports', icon: Download },
    { id: 'dashboard', label: 'Insights', icon: TrendingUp },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto" data-testid="inventory-management">
      <h1 className="text-xl font-bold text-gray-900 mb-1">Inventory & POS</h1>
      <p className="text-sm text-gray-500 mb-4">Manage consumables, track stock, and record sales</p>

      {/* Tab Bar */}
      <div className="flex overflow-x-auto gap-1 mb-5 pb-1 scrollbar-hide" data-testid="inventory-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id ? 'bg-[#0A2C93] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'products' && <ProductsTab products={products} onRefresh={loadProducts} categories={categories} onAddCategory={handleAddCategory} />}
      {activeTab === 'pos' && <POSTab products={products} onRefresh={loadProducts} />}
      {activeTab === 'purchases' && <PurchasesTab products={products} onRefresh={loadProducts} />}
      {activeTab === 'rentals' && <RentalsTab products={products} onRefresh={loadProducts} />}
      {activeTab === 'reports' && <ReportsTab />}
      {activeTab === 'dashboard' && <DashboardTab products={products} />}
    </div>
  );
};

export default InventoryManagement;
